/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import styles from './PerpetualFeedCard.module.scss';

import type { FeedAlert, PerpetualFeedCardProps } from './PerpetualFeedCard.types';

const CLOCK_PLACEHOLDER = '--:--:--';

const pad = (value: number) => `${value < 10 ? '0' : ''}${value}`;

const utcClock = (date: Date) => `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;

const utcStamp = (date: Date) => `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;

type FeedRow = {
  uid: number;
  incoming: boolean;
  alert: FeedAlert;
};

export const PerpetualFeedCard = ({
  alerts,
  label = 'Perpetual KYB · live',
  visibleCount = 5,
  monitoredCount,
  footerNote,
  className,
}: PerpetualFeedCardProps) => {
  const seed = alerts.slice(0, visibleCount);

  const [rows, setRows] = useState<FeedRow[]>(() =>
    seed
      .map((alert, index) => ({ uid: index, incoming: false, alert }))
      .slice()
      .reverse(),
  );
  const [changeCount, setChangeCount] = useState(seed.length);
  const [clock, setClock] = useState(CLOCK_PLACEHOLDER);
  const [openUid, setOpenUid] = useState<number | null>(null);

  const nextIndex = useRef(seed.length);
  const nextUid = useRef(seed.length);
  const paused = useRef(false);

  useEffect(() => {
    const tick = () => setClock(utcClock(new Date()));

    tick();

    const id = window.setInterval(tick, 1000);

    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!alerts.length) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let timeout: number;

    const schedule = () => {
      timeout = window.setTimeout(
        () => {
          if (!paused.current && !document.hidden) {
            const alert = alerts[nextIndex.current % alerts.length];

            nextIndex.current += 1;

            const uid = nextUid.current;

            nextUid.current += 1;

            setRows((current) =>
              [{ uid, incoming: true, alert: { ...alert, time: utcStamp(new Date()) } }, ...current].slice(
                0,
                visibleCount,
              ),
            );
            setChangeCount((current) => current + 1);
          }

          schedule();
        },
        2000 + Math.random() * 1000,
      );
    };

    schedule();

    return () => window.clearTimeout(timeout);
  }, [alerts, visibleCount]);

  const toggle = useCallback((uid: number) => setOpenUid((current) => (current === uid ? null : uid)), []);

  return (
    <div className={cn(styles.card, className)} role="region" aria-label="Live portfolio monitoring feed">
      <span className={styles.scanLine} aria-hidden="true" />

      <div className={styles.head}>
        <span>
          <span className={styles.dot} aria-hidden="true" />
          {label}
        </span>

        <span className={styles.headRight}>
          <span className={styles.clock}>{clock}</span> UTC
        </span>
      </div>

      <div className={styles.cols} aria-hidden="true">
        <span>Company</span>
        <span>Change</span>
        <span className={styles.colsRight}>Detected</span>
      </div>

      <div
        className={styles.feed}
        onMouseEnter={() => {
          paused.current = true;
        }}
        onMouseLeave={() => {
          paused.current = false;
        }}
      >
        {rows.map(({ uid, incoming, alert }) => {
          const isOpen = openUid === uid;

          return (
            <div
              key={uid}
              className={cn(styles.row, incoming && styles.rowIncoming, isOpen && styles.rowOpen)}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={() => toggle(uid)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggle(uid);
                }
              }}
            >
              <div className={styles.main}>
                <div>
                  <span className={styles.entity}>{alert.company}</span>
                  <span className={styles.source}>{alert.registry}</span>
                </div>

                <div>
                  <span className={cn(styles.badge, styles[`badge_${alert.variant}`])}>{alert.change}</span>
                </div>

                <div className={styles.time}>
                  {alert.time} UTC
                  <span className={styles.chevron} aria-hidden="true" />
                </div>
              </div>

              <div className={styles.detail}>
                <dl className={styles.detailInner}>
                  <dt>Filing</dt>
                  <dd>{alert.filing}</dd>

                  <dt>Change</dt>
                  <dd>
                    {alert.from}
                    <span className={styles.arrow}>→</span>
                    {alert.to}
                  </dd>

                  <dt>Source</dt>
                  <dd className={styles.detailMuted}>{`${alert.registry} · ${alert.reference}`}</dd>
                </dl>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.foot}>
        <span className={styles.footLeft}>
          <span className={cn(styles.dot, styles.dotSm)} aria-hidden="true" />
          <b>{changeCount}</b> changes today
          {monitoredCount && (
            <>
              {' · '}
              <b>{monitoredCount}</b> monitored
            </>
          )}
        </span>

        {footerNote && <span className={styles.footRight}>{footerNote}</span>}
      </div>
    </div>
  );
};
